import {
  AiOnboardingDocumentUploadResult,
  AiOnboardingDocumentType,
  uploadAiOnboardingDocument,
} from '@pharmaplan/common';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAppDispatch } from '../useAppDispatch';

interface IUseAiDocumentUpload {
  signupRequestId: string | null;
}

interface IActiveUpload {
  documentType: AiOnboardingDocumentType;
  requestId: number;
  signupRequestId: string;
}

interface IPendingUpload {
  requestId: number;
  resolve: (result: AiOnboardingDocumentUploadResult) => void;
}

const NOT_UPLOADED_RESULT: AiOnboardingDocumentUploadResult = {
  uploaded: false,
};

const useAiDocumentUpload = ({
  signupRequestId,
}: IUseAiDocumentUpload) => {
  const dispatch = useAppDispatch();
  const activeUploadRef = useRef<IActiveUpload | null>(null);
  const isMountedRef = useRef(true);
  const nextRequestIdRef = useRef(0);
  const pendingUploadRef = useRef<IPendingUpload | null>(null);
  const profilePictureFileRef = useRef<File | null>(null);
  const signupRequestIdRef = useRef(signupRequestId);
  const [activeUpload, setActiveUpload] = useState<IActiveUpload | null>(null);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  signupRequestIdRef.current = signupRequestId;

  const clearActiveUpload = useCallback(() => {
    activeUploadRef.current = null;

    if (isMountedRef.current) {
      setActiveUpload(null);
      setHasUploadError(false);
      setIsUploading(false);
    }
  }, []);

  const resolveUpload = useCallback((
    requestId: number,
    result: AiOnboardingDocumentUploadResult,
  ) => {
    const pendingUpload = pendingUploadRef.current;

    if (!pendingUpload || pendingUpload.requestId !== requestId) {
      return;
    }

    pendingUploadRef.current = null;
    pendingUpload.resolve(result);
    clearActiveUpload();
  }, [clearActiveUpload]);

  const cancelPendingUpload = useCallback(() => {
    const pendingUpload = pendingUploadRef.current;

    if (!pendingUpload) {
      clearActiveUpload();
      return;
    }

    resolveUpload(pendingUpload.requestId, NOT_UPLOADED_RESULT);
  }, [clearActiveUpload, resolveUpload]);

  const openDocumentUpload = useCallback(
    (documentType: AiOnboardingDocumentType) => {
      const currentSignupRequestId = signupRequestIdRef.current;

      if (!isMountedRef.current || !currentSignupRequestId) {
        return Promise.resolve(NOT_UPLOADED_RESULT);
      }

      cancelPendingUpload();
      nextRequestIdRef.current += 1;

      const nextUpload = {
        documentType,
        requestId: nextRequestIdRef.current,
        signupRequestId: currentSignupRequestId,
      };

      activeUploadRef.current = nextUpload;
      setActiveUpload(nextUpload);
      setHasUploadError(false);
      setIsUploading(false);

      return new Promise<AiOnboardingDocumentUploadResult>((resolve) => {
        pendingUploadRef.current = {
          requestId: nextUpload.requestId,
          resolve,
        };
      });
    },
    [cancelPendingUpload],
  );

  const handleClose = useCallback(() => {
    cancelPendingUpload();
  }, [cancelPendingUpload]);

  const handleUpload = useCallback(async (file: File) => {
    const currentUpload = activeUploadRef.current;

    if (
      !currentUpload
      || typeof File === 'undefined'
      || !(file instanceof File)
    ) {
      cancelPendingUpload();
      return;
    }

    setHasUploadError(false);
    setIsUploading(true);

    if (currentUpload.documentType === 'profilePicture') {
      profilePictureFileRef.current = file;
      resolveUpload(currentUpload.requestId, { uploaded: true });
      return;
    }

    const form = new FormData();
    form.append('file', file);

    try {
      await dispatch(
        uploadAiOnboardingDocument({
          signupRequestId: currentUpload.signupRequestId,
          documentType: currentUpload.documentType,
          form,
        }),
      ).unwrap();

      resolveUpload(currentUpload.requestId, { uploaded: true });
    } catch {
      const isCurrentUpload = activeUploadRef.current?.requestId
        === currentUpload.requestId;

      if (isMountedRef.current && isCurrentUpload) {
        setHasUploadError(true);
        setIsUploading(false);
      }
    }
  }, [cancelPendingUpload, dispatch, resolveUpload]);

  const getProfilePictureFile = useCallback(
    () =>
      profilePictureFileRef.current,
    [],
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      const pendingUpload = pendingUploadRef.current;
      pendingUploadRef.current = null;
      activeUploadRef.current = null;
      pendingUpload?.resolve(NOT_UPLOADED_RESULT);
    };
  }, []);

  useEffect(
    () => {
      profilePictureFileRef.current = null;

      return () => {
        cancelPendingUpload();
        profilePictureFileRef.current = null;
      };
    },
    [cancelPendingUpload, signupRequestId],
  );

  return {
    dialogProps: {
      documentType: activeUpload?.documentType ?? null,
      hasUploadError,
      isOpen: Boolean(activeUpload),
      isUploading,
      onClose: handleClose,
      onUpload: handleUpload,
      requestId: activeUpload?.requestId ?? null,
    },
    getProfilePictureFile,
    openDocumentUpload,
  };
};

export default useAiDocumentUpload;
